import React, { useEffect, useMemo, useState } from 'react';
import Sidebar from './Sidebar';
import { apiService } from '@/utils/api';
import type { ManualResponse } from '@/types/api';
import type { WirelessAddOns } from '@/types/deviceSetup';
import type { AuxAssign, EquipmentItem } from '@/types/dashboard';
import { FiChevronLeft, FiCheckCircle, FiLock } from 'react-icons/fi';
import { AuxTypeCode, canBeTimerForAssignEquipment } from '@/types/device';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { isTimerHourMinuteSupported, isTimerWirelessButtonSupported } from '@/utils/firmware';
import { formatAuxTimerDuration, getAuxTimerSeconds } from '@/utils/auxTimer';

export interface AssignEquipmentSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  deviceId: string;
  wirelessAddOns?: WirelessAddOns;
  auxAssign?: AuxAssign;
  onRefresh?: () => void | Promise<void>;
  initialMode?: 'aux' | 'pump';
  initialSlotIndex?: number;
  firmwareVersion?: string;
}

type BoardSelection = 'main' | 'expander_1' | 'expander_2';
type SlotItem = { index: number; label: string; occupied: boolean; assignedName?: string; isRs485?: boolean };

const LEGACY_MAX_AUX_TIMER_MINUTES = 29;
const LEGACY_MAX_AUX_TIMER_SECONDS = 59;
const HM_MAX_AUX_TIMER_HOURS = 5;
const HM_MAX_AUX_TIMER_MINUTES = 59;
const RS485_NAME = 'RS485';
const RS485_BRAND = 'Mineral Swim V2';

const parseTimerInput = (value: string, max: number) => {
  const digitsOnly = value.replace(/\D/g, '');
  if (!digitsOnly) return 0;
  return Math.max(0, Math.min(max, Number.parseInt(digitsOnly, 10) || 0));
};

const AssignEquipmentSidebar: React.FC<AssignEquipmentSidebarProps> = ({
  isOpen,
  onClose,
  deviceId,
  wirelessAddOns,
  auxAssign,
  onRefresh,
  initialMode,
  initialSlotIndex,
  firmwareVersion,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [manual, setManual] = useState<ManualResponse['manual'] | null>(null);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selection, setSelection] = useState<BoardSelection | null>(null);
  const [selectedFunction, setSelectedFunction] = useState<string | null>(null);
  const [availableEquipmentList, setAvailableEquipmentList] = useState<number[]>([]);
  const [equipmentCategoryList, setEquipmentCategoryList] = useState<string[]>([]);
  const [currentType, setCurrentType] = useState<number>(0);
  const [isSubLoading, setIsSubLoading] = useState<boolean>(false);
  // Pump menu states
  const [pumpCategory, setPumpCategory] = useState<string | null>(null);
  const [pumpBrand, setPumpBrand] = useState<string | null>(null);
  const [pumpModel, setPumpModel] = useState<string | null>(null);
  // Pump menu constants
  const pumpLevel1 = useMemo<string[]>(() => ['SplashMe VSD', '3 Speed', 'Inverter/VFD', 'Others'], []);
  const speed3BrandList = useMemo<string[]>(() => ['Reltech', 'Pool Pro', 'Maytronics', 'Pentair'], []);
  const speed3ModelMap = useMemo<Record<string, string[]>>(() => ({
    Reltech: ['V3 PRO+', 'V5 PRO+', 'V8 PRO+'],
    'Pool Pro': ['X-ECO', 'X-PRO'],
    Maytronics: ['ecoclear-1400vs', 'Ecoclear-900VS'],
    Pentair: ['Whisperflow-800VS'],
  }), []);
  const inverterBrandList = useMemo<string[]>(() => ['AQUAGEM'], []);
  const inverterModelMap = useMemo<Record<string, string[]>>(() => ({ AQUAGEM: ['ISAVERX1100'] }), []);
  const pumpOtherType = useMemo<string[]>(() => ['Single Speed'], []);
  const equipmentFunctionList = useMemo(() => ['Sanitisation', 'Heating', 'General', 'Pool Lights', 'Spa', 'Spa Lights'], []);
  const auxillaryFunctionList = useMemo(
    () => [
      [
        'Salt Chlorinator',
        'pH Doser',
        'Mineral Chlorinator',
        'Liquid Chlorine',
        'Naked(Loniser)',
        'Ozone',
        'UV',
      ],
      ['Solar Dedicated', 'Solar Filter Pump', 'Pool Heater', 'Spa Heater', 'Spa & Pool Heater'],
      ['Auxiliary', 'Pressure Cleaner', 'In Floor Cleaning'],
      ['AquaQuip', 'Spa Electrics', 'PAL Lighting'],
      [
        'Spa Actuator Suction',
        'Spa Actuator Return',
        'Spa Jets 1',
        'Spa Jets 2',
        'Spa Blower 1',
        'Spa Blower 2',
      ],
      ['AquaQuip', 'Spa Electrics', 'PAL Lighting'],
    ],
    []
  );
  const poolLightCodes = useMemo(() => [
    AuxTypeCode.ASTRAL_POOL_LIGHT,
    AuxTypeCode.AQUAQUIP_LIGHT,
    AuxTypeCode.POOLRITE_LIGHT,
    AuxTypeCode.SPA_ELECTRICS_LIGHT,
    AuxTypeCode.ZODIAC,
    AuxTypeCode.PAL_LIGHTING,
  ], []);
  const spaLightCodes = useMemo(() => [
     AuxTypeCode.SPA_ASTRAL_POOL_LIGHT,
     AuxTypeCode.SPA_AQUAQUIP_LIGHT,
     AuxTypeCode.SPA_POOLRITE_LIGHT,
     AuxTypeCode.SPA_SPA_ELECTRICS_LIGHT,
     AuxTypeCode.SPA_ZODIAC,
     AuxTypeCode.SPA_PAL_LIGHTING,
   ], []);
  const hasAssignedLight = (kind: 'pool' | 'spa') => {
    const target = kind === 'pool' ? poolLightCodes : spaLightCodes;
    const auxList: any[] = (manual?.aux as any[]) || [];
    return auxList.some((a) => target.includes(a?.aux_type_code));
  };
  const isFunctionDisabled = (label: string) => {
    if (label === 'Pool Lights') return hasAssignedLight('pool') || (selection === 'expander_1' || selection === 'expander_2');
    if (label === 'Spa Lights') return hasAssignedLight('spa') || (selection === 'expander_1' || selection === 'expander_2');
    return false;
  };
  const [selectedSlot, setSelectedSlot] = useState<SlotItem | null>(null);


  useEffect(() => {
    if (!isOpen || !deviceId) return;
    // 从 Pump 卡片进入并处于泵选择步骤时，跳过 manual 拉取
    if (isPumpContext) {
       setIsLoading(false);
      return;
    }
    let mounted = true;
    setIsLoading(true);
    apiService
      .get<ManualResponse>(`/api/v1/iot-shadow/device/${deviceId}/manual`)
      .then((resp) => {
        if (!mounted) return;
        setManual(resp.manual);
      })
      .catch((e) => {
        console.error('Failed to fetch manual:', e);
      })
      .finally(() => {
        if (!mounted) return;
        setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [isOpen, deviceId, step, selectedSlot]);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSelection(null);
    }
  }, [isOpen]);

  const hasExpander1 = useMemo(() => {
    const paired = wirelessAddOns?.paired_lora_device || [];
    return paired.some((d) => d.connected && d.expander_idx === 1) || !!wirelessAddOns?.expander_1_uuid;
  }, [wirelessAddOns]);

  const hasExpander2 = useMemo(() => {
    const paired = wirelessAddOns?.paired_lora_device || [];
    return paired.some((d) => d.connected && d.expander_idx === 2);
  }, [wirelessAddOns]);

  const typeOptions: { id: BoardSelection; label: string }[] = useMemo(() => {
    const opts: { id: BoardSelection; label: string }[] = [
      { id: 'main', label: 'SplashMe Auxiliaries' },
    ];
    if (hasExpander1) opts.push({ id: 'expander_1', label: 'Power Expander - 1' });
    if (hasExpander2) opts.push({ id: 'expander_2', label: 'Power Expander - 2' });
    return opts;
  }, [hasExpander1, hasExpander2]);

  const splashMeLabels = [
    'Filter Pump',
    'Auxiliary 1',
    'Auxiliary 2',
    'Auxiliary 3',
    'Auxiliary 4',
    'Auxiliary 5',
    'Auxiliary 6',
    'Auxiliary 7',
    'Auxiliary 8',
    'Auxiliary 9',
    'Auxiliary 10',
    'Auxiliary 11',
    RS485_NAME,
  ];
  const expander1Labels = [
    'Expander 1 Auxiliary 1',
    'Expander 1 Auxiliary 2',
    'Expander 1 Auxiliary 3',
    'Expander 1 Auxiliary 4',
    'Expander 1 Auxiliary 5',
  ];
  const expander2Labels = [
    'Expander 2 Auxiliary 1',
    'Expander 2 Auxiliary 2',
    'Expander 2 Auxiliary 3',
    'Expander 2 Auxiliary 4',
    'Expander 2 Auxiliary 5',
  ];

  const getAssignedName = (idx: number): string | undefined => {
    const names = auxAssign?.aux_name;
    if (Array.isArray(names) && names[idx]) return names[idx];
    const eqList = auxAssign?.equipment || [];
    const eq = (eqList as EquipmentItem[]).find((e) => e.slot === idx && (e?.type_code ?? 0) !== 0);
    return eq?.label || undefined;
  };

  const boardSlotItems: SlotItem[] = useMemo(() => {
    const status = auxAssign?.aux_assign || [];
    if (selection === 'main') {
      return splashMeLabels.map((label, i) => {
        if (label === RS485_NAME) {
          return {
            index: i,
            label,
            occupied: !!auxAssign?.has_ecoclear,
            assignedName: auxAssign?.has_ecoclear ? RS485_BRAND : undefined,
            isRs485: true,
          };
        }

        return {
          index: i,
          label,
          occupied: (status[i] ?? 0) !== 0,
          assignedName: (status[i] ?? 0) !== 0 ? getAssignedName(i) : undefined,
        };
      });
    }
    if (selection === 'expander_1') {
      return expander1Labels.map((label, i) => {
        const idx = 12 + i;
        return { index: idx, label, occupied: (status[idx] ?? 0) !== 0, assignedName: (status[idx] ?? 0) !== 0 ? getAssignedName(idx) : undefined };
      });
    }
    if (selection === 'expander_2') {
      return expander2Labels.map((label, i) => {
        const idx = 17 + i;
        return { index: idx, label, occupied: (status[idx] ?? 0) !== 0, assignedName: (status[idx] ?? 0) !== 0 ? getAssignedName(idx) : undefined };
      });
    }
    return [];
  }, [selection, auxAssign?.aux_assign, auxAssign?.aux_name, auxAssign?.equipment, auxAssign?.has_ecoclear]);

  const handleAvailableAux = (result: number[], typeList: string[]): number[] => {
    const output: number[] = [];
    const len = Math.min(typeList.length, result.length);
    for (let i = 0; i < len; i++) {
      output.push(result[i] !== 0 ? result[i] : 0);
    }
    for (let j = len; j < typeList.length; j++) {
      output.push(0);
    }
    return output;
  };

  const handleFunctionClick = async (func: string) => {
    if (!selectedSlot) return;
    if (isFunctionDisabled(func)) return;
    setSelectedFunction(func);

    // 切换一级 Function 时，立即清空二级内容
    setEquipmentCategoryList([]);
    setAvailableEquipmentList([]);
    setCurrentType(0);

    if (selectedSlot.isRs485) {
      setEquipmentCategoryList([RS485_BRAND]);
      setAvailableEquipmentList([AuxTypeCode.RS485]);
      setCurrentType(auxAssign?.has_ecoclear ? AuxTypeCode.RS485 : 0);
      setIsSubLoading(false);
      return;
    }

    // 灯光无二级，不显示加载骨架，直接返回
    if (func === 'Pool Lights' || func === 'Spa Lights') {
      const type = func === 'Pool Lights' ? 'pool' : 'spa';
      console.log('Navigate to light assignment', { auxIndex: selectedSlot.index, type });
      setIsSubLoading(false);
      return;
    }

    // 显示加载骨架直到新数据返回
    setIsSubLoading(true);

    const position = equipmentFunctionList.indexOf(func);
    try {
      const resp: any = await apiService.post(`/api/v1/iot-shadow/device/${deviceId}/getAuxConflict`, {
        AuxNum: selectedSlot.index,
        Category: position,
      });
      const message = resp?.data?.message || resp?.message || resp;
      const typeListResp: number[] = message?.type_list || message?.TypeList || [];
      const currentSelected: number = message?.current_type ?? message?.CurrentType ?? 0;
      const categories = auxillaryFunctionList[position] || [];
      setEquipmentCategoryList(categories);
      const filtered = handleAvailableAux(typeListResp, categories);
      setAvailableEquipmentList(filtered);
      setCurrentType(currentSelected);
      console.log('Available types for', func, filtered, 'current:', currentSelected);
    } catch (e) {
      console.error('Failed to get aux conflict:', e);
    } finally {
      setIsSubLoading(false);
    }
  };

  const handleSelectType = (id: BoardSelection) => {
    setSelection(id);
    setStep(2);
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(2);
      setSelectedSlot(null);
      setSelectedFunction(null);
      setAvailableEquipmentList([]);
      setEquipmentCategoryList([]);
      setCurrentType(0);
      setIsSubLoading(false);
      // reset pump states
      setPumpCategory(null);
      setPumpBrand(null);
      setPumpModel(null);
      return;
    }
    setStep(1);
    setSelection(null);
    setSelectedSlot(null);
    setSelectedFunction(null);
    setAvailableEquipmentList([]);
    setEquipmentCategoryList([]);
    setCurrentType(0);
    setIsSubLoading(false);
    setPumpCategory(null);
    setPumpBrand(null);
    setPumpModel(null);
  };

  const handleSlotSelect = (item: SlotItem) => {
    if (item.occupied) return;
    setSelectedSlot(item);
    setSelectedFunction(null);
    setAvailableEquipmentList([]);
    setEquipmentCategoryList([]);
    setCurrentType(0);
    setIsSubLoading(false);
    setStep(3);
  };

  // Open directly in pump mode when requested
  useEffect(() => {
    if (!isOpen) return;
    if (initialMode === 'pump') {
      setSelection('main');
      const idx = typeof initialSlotIndex === 'number' ? initialSlotIndex : 0;
      const status = auxAssign?.aux_assign || [];
      const occupied = (status[idx] ?? 0) !== 0;
      setSelectedSlot({ index: idx, label: 'Filter Pump', occupied, assignedName: occupied ? getAssignedName(idx) : undefined });
      setStep(3);
    }
  }, [isOpen, initialMode, initialSlotIndex, auxAssign]);

  const isPumpContext = (selectedSlot?.label === 'Filter Pump') || initialMode === 'pump';

  const [confirmPumpOpen, setConfirmPumpOpen] = useState<boolean>(false);
  const [pendingPumpName, setPendingPumpName] = useState<string | null>(null);
  const [timerConfigOpen, setTimerConfigOpen] = useState(false);
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [timerHours, setTimerHours] = useState(0);
  const [timerMinutes, setTimerMinutes] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerTarget, setTimerTarget] = useState<{
    auxNum: number;
    typeCode: number;
    auxName: string;
    lightTypeMode: number;
    title: string;
  } | null>(null);

  const timerFeatureEnabled = useMemo(
    () => isTimerWirelessButtonSupported(firmwareVersion),
    [firmwareVersion],
  );
  const timerHourMinuteSupported = useMemo(
    () => isTimerHourMinuteSupported(firmwareVersion),
    [firmwareVersion],
  );

  const submitRs485Assign = async (enabled: boolean) => {
    setCurrentType(enabled ? AuxTypeCode.RS485 : 0);
    setIsSubLoading(true);
    try {
      await apiService.post(`/api/v1/iot-shadow/device/${deviceId}/setRs485Assign`, {
        enabled,
      });
    } catch (e) {
      console.error('Failed to set RS485 assign:', e);
    } finally {
      setIsSubLoading(false);
      await resetAssignFlow();
    }
  };

  const resetAssignFlow = async () => {
    setStep(2);
    setSelection('main');
    setSelectedSlot(null);
    setSelectedFunction(null);
    setEquipmentCategoryList([]);
    setAvailableEquipmentList([]);
    setCurrentType(0);
    if (typeof onRefresh === 'function') {
      try {
        await onRefresh();
      } catch (e) {
        console.warn('onRefresh failed:', e);
      }
    }
  };

  const submitAuxAssign = async ({
    auxNum,
    typeCode,
    auxName,
    lightTypeMode,
    auxTimerDurationSec = 0,
  }: {
    auxNum: number;
    typeCode: number;
    auxName: string;
    lightTypeMode: number;
    auxTimerDurationSec?: number;
  }) => {
    setCurrentType(typeCode);
    setIsSubLoading(true);
    try {
      await apiService.post(`/api/v1/iot-shadow/device/${deviceId}/setAuxAssign`, {
        aux_num: auxNum,
        type_code: typeCode,
        aux_name: auxName,
        light_type_mode: lightTypeMode,
        aux_timer_duration_sec: auxTimerDurationSec,
      });
      console.log('SetAuxAssign done', {
        aux_num: auxNum,
        type_code: typeCode,
        aux_name: auxName,
        light_type_mode: lightTypeMode,
        aux_timer_duration_sec: auxTimerDurationSec,
      });
    } catch (e) {
      console.error('Failed to set aux assign:', e);
    } finally {
      setIsSubLoading(false);
      await resetAssignFlow();
    }
  };

  const openTimerConfig = ({
    auxNum,
    typeCode,
    auxName,
    lightTypeMode,
    title,
  }: {
    auxNum: number;
    typeCode: number;
    auxName: string;
    lightTypeMode: number;
    title: string;
  }) => {
    const existingTimerSeconds = getAuxTimerSeconds(auxAssign?.aux_timer, auxNum);
    setTimerTarget({ auxNum, typeCode, auxName, lightTypeMode, title });
    setTimerEnabled(existingTimerSeconds > 0);
    if (timerHourMinuteSupported) {
      const totalMinutes = Math.floor(existingTimerSeconds / 60);
      setTimerHours(Math.min(HM_MAX_AUX_TIMER_HOURS, Math.floor(totalMinutes / 60)));
      setTimerMinutes(Math.min(HM_MAX_AUX_TIMER_MINUTES, totalMinutes % 60));
      setTimerSeconds(0);
    } else {
      setTimerHours(0);
      setTimerMinutes(Math.min(LEGACY_MAX_AUX_TIMER_MINUTES, Math.floor(existingTimerSeconds / 60)));
      setTimerSeconds(Math.min(LEGACY_MAX_AUX_TIMER_SECONDS, existingTimerSeconds % 60));
    }
    setTimerConfigOpen(true);
  };

  const closeTimerConfig = () => {
    setTimerConfigOpen(false);
    setTimerTarget(null);
    setTimerEnabled(false);
    setTimerHours(0);
    setTimerMinutes(0);
    setTimerSeconds(0);
  };

  const handleConfirmTimerAssign = async () => {
    if (!timerTarget) return;
    const durationSeconds = timerEnabled
      ? Math.max(
          0,
          timerHourMinuteSupported ? timerHours * 3600 + timerMinutes * 60 : timerMinutes * 60 + timerSeconds
        )
      : 0;
    if (timerEnabled && durationSeconds <= 0) {
      return;
    }
    setTimerConfigOpen(false);
    await submitAuxAssign({
      auxNum: timerTarget.auxNum,
      typeCode: timerTarget.typeCode,
      auxName: timerTarget.auxName,
      lightTypeMode: timerTarget.lightTypeMode,
      auxTimerDurationSec: durationSeconds,
    });
    closeTimerConfig();
  };

  const handleSavePumpAssign = (auxName: string) => {
    setPendingPumpName(auxName);
    setConfirmPumpOpen(true);
  };

  const confirmPumpAssign = async () => {
    if (!selectedSlot || !pendingPumpName) {
      setConfirmPumpOpen(false);
      return;
    }

    // 构造 SetPump 请求参数
    const typeStr = pendingPumpName === 'SplashMe VSD' ? 'SplashMe VSD' : (pumpCategory ?? 'Others');
    const brand = typeStr === 'SplashMe VSD' ? '' : (typeStr === 'Others' ? 'Single Speed' : (pumpBrand ?? ''));
    const model = typeStr === 'SplashMe VSD' ? '' : (typeStr === 'Others' ? '' : (pumpModel ?? ''));

    setIsSubLoading(true);
    try {
      await apiService.post(`/api/v1/iot-shadow/device/${deviceId}/setPump`, {
        type_str: typeStr,
        brand,
        model,
      });
      console.log('SetPump done', { type_str: typeStr, brand, model });
    } catch (e) {
      console.error('Failed to set pump:', e);
    } finally {
      setIsSubLoading(false);
      setConfirmPumpOpen(false);
      setPendingPumpName(null);
      setStep(2);
      setSelection('main');
      setSelectedSlot(null);
      setSelectedFunction(null);
      setEquipmentCategoryList([]);
      setAvailableEquipmentList([]);
      setCurrentType(0);
      setPumpCategory(null);
      setPumpBrand(null);
      setPumpModel(null);
      if (typeof onRefresh === 'function') {
        try { await onRefresh(); } catch (e) { console.warn('onRefresh failed:', e); }
      }
      // 请求结束后关闭侧边栏
      onClose();
    }
  };

  return (
    <Sidebar
      isOpen={isOpen}
      onClose={onClose}
      title={step === 1 ? 'Assign Equipment' : step === 2 ? 'Select Auxiliary Slot' : 'Select Function'}
      isLoading={isLoading}
    >
      {step === 1 ? (
        <div className="space-y-3">
          <div className="text-sm text-gray-600 dark:text-gray-300">Select board to assign</div>
          <div className="space-y-2">
            {typeOptions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className="w-full p-3 rounded-md border flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800"
                onClick={() => handleSelectType(opt.id)}
              >
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{opt.label}</span>
              </button>
            ))}
          </div>
        </div>
      ) : step === 2 ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={handleBack}
            >
              <FiChevronLeft className="h-4 w-4" /> Back
            </button>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              {selection === 'main' ? 'SplashMe Auxiliaries' : selection === 'expander_1' ? 'Power Expander - 1' : 'Power Expander - 2'}
            </div>
          </div>

          {boardSlotItems.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">No slots available.</div>
          ) : (
            <div className="space-y-2">
              {boardSlotItems.map((item) => (
                <div
                  key={item.index}
                  className={`w-full p-3 rounded-md border flex items-center justify-between ${item.occupied ? 'bg-gray-100 dark:bg-gray-800 opacity-60 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.label}</span>
                    {item.occupied && item.assignedName ? (
                      <span className="text-xs text-gray-600 dark:text-gray-400">{item.assignedName}</span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={`px-2 py-1 rounded text-xs font-medium ${item.occupied ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'text-white bg-blue-600 hover:bg-blue-700'}`}
                    disabled={item.occupied}
                    onClick={() => handleSlotSelect(item)}
                  >
                    {item.occupied ? 'Occupied' : 'Select'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={handleBack}
            >
              <FiChevronLeft className="h-4 w-4" /> Back
            </button>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Select Function for {selectedSlot?.label}
            </div>
          </div>

          <div className="space-y-2">
            {isPumpContext ? (
              <div className="w-full rounded-md border p-3">
                {/* Level 1: Pump type */}
                <div className="space-y-2">
                  {pumpLevel1.map((label: string) => (
                    <button
                      key={label}
                      type="button"
                      className={`w-full p-2 rounded-md border ${pumpCategory === label ? 'bg-green-50 dark:bg-green-900/20 ring-2 ring-green-500 border-green-400' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                      onClick={() => {
                        if (label === 'SplashMe VSD') {
                          handleSavePumpAssign('SplashMe VSD');
                        } else {
                          setPumpCategory(label);
                          setPumpBrand(null);
                          setPumpModel(null);
                        }
                      }}
                    >
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
                    </button>
                  ))}
                </div>

                {/* Level 2: Brand/Model based on category */}
                {pumpCategory === '3 Speed' && (
                  <div className="mt-4 space-y-3">
                    <div className="text-xs uppercase tracking-wide text-blue-600 dark:text-blue-400 font-semibold">Step 1 · Brand</div>
                    <div className="space-y-2">
                      {speed3BrandList.map((b: string) => (
                        <button
                          key={b}
                          type="button"
                          className={`w-full p-2 rounded-md border ${pumpBrand === b ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 ring-2 ring-blue-500' : 'border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                          onClick={() => { setPumpBrand(b); setPumpModel(null); }}
                        >
                          <span className="text-sm font-medium">{b}</span>
                        </button>
                      ))}
                    </div>
                    {pumpBrand && (
                      <div className="mt-3 space-y-2">
                        <div className="text-xs uppercase tracking-wide text-purple-600 dark:text-purple-400 font-semibold">Step 2 · Model</div>
                        <div className="flex flex-wrap gap-2">
                          <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">Brand: {pumpBrand}</span>
                        </div>
                        {(speed3ModelMap[pumpBrand as string] || []).map((m: string) => (
                          <button
                            key={m}
                            type="button"
                            className={`w-full p-2 rounded-md border ${pumpModel === m ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 ring-2 ring-purple-500' : 'border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                            onClick={() => { setPumpModel(m); handleSavePumpAssign(`${pumpBrand} ${m}`); }}
                          >
                            <span className="text-sm font-medium">{m}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {pumpCategory === 'Inverter/VFD' && (
                  <div className="mt-4 space-y-3">
                    <div className="text-xs uppercase tracking-wide text-blue-600 dark:text-blue-400 font-semibold">Step 1 · Brand</div>
                    <div className="space-y-2">
                      {inverterBrandList.map((b: string) => (
                        <button
                          key={b}
                          type="button"
                          className={`w-full p-2 rounded-md border ${pumpBrand === b ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 ring-2 ring-blue-500' : 'border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                          onClick={() => { setPumpBrand(b); setPumpModel(null); }}
                        >
                          <span className="text-sm font-medium">{b}</span>
                        </button>
                      ))}
                    </div>
                    {pumpBrand && (
                      <div className="mt-3 space-y-2">
                        <div className="text-xs uppercase tracking-wide text-purple-600 dark:text-purple-400 font-semibold">Step 2 · Model</div>
                        <div className="flex flex-wrap gap-2">
                          <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">Brand: {pumpBrand}</span>
                        </div>
                        {(inverterModelMap[pumpBrand as string] || []).map((m: string) => (
                          <button
                            key={m}
                            type="button"
                            className={`w-full p-2 rounded-md border ${pumpModel === m ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 ring-2 ring-purple-500' : 'border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                            onClick={() => { setPumpModel(m); handleSavePumpAssign(`${pumpBrand} ${m}`); }}
                          >
                            <span className="text-sm font-medium">{m}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {pumpCategory === 'Others' && (
                  <div className="mt-4 space-y-3">
                    <div className="text-xs uppercase tracking-wide text-amber-600 dark:text-amber-400 font-semibold">Type</div>
                    <div className="space-y-2">
                      {pumpOtherType.map((t) => (
                        <button
                          key={t}
                          type="button"
                          className="w-full p-2 rounded-md border border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 text-amber-700 dark:text-amber-300"
                          onClick={() => handleSavePumpAssign(t)}
                        >
                          <span className="text-sm font-medium">{t}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {isSubLoading && (
                  <div className="mt-3 space-y-2 animate-pulse">
                    <div className="h-5 rounded bg-gray-200 dark:bg-gray-700"></div>
                    <div className="h-5 rounded bg-gray-200 dark:bg-gray-700"></div>
                  </div>
                )}
              </div>
            ) : (
              (selectedSlot?.isRs485 ? ['Sanitisation'] : equipmentFunctionList).map((label) => {
                const disabled = isFunctionDisabled(label);
                const isExpanded = selectedFunction === label;
                return (
                  <div key={label} className="w-full rounded-md border">
                    <button
                      type="button"
                      className={`w-full p-3 flex items-center justify-between ${disabled ? 'bg-gray-100 dark:bg-gray-800 opacity-60 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                      disabled={disabled}
                      onClick={() => handleFunctionClick(label)}
                    >
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
                    </button>
                    {isExpanded && equipmentCategoryList.length > 0 ? (
                      <div className="px-3 pb-3 space-y-1">
                        {isSubLoading ? (
                          <div className="space-y-2 animate-pulse">
                            <div className="h-5 rounded bg-gray-200 dark:bg-gray-700"></div>
                            <div className="h-5 rounded bg-gray-200 dark:bg-gray-700"></div>
                            <div className="h-5 rounded bg-gray-200 dark:bg-gray-700"></div>
                          </div>
                        ) : equipmentCategoryList.length > 0 ? (
                          equipmentCategoryList.map((subLabel, idx) => {
                            const code = availableEquipmentList[idx] ?? 0;
                            const subDisabled = code === 0;
                            const selected = currentType !== 0 && code === currentType;
                            return (
                              <button
                                key={`${subLabel}-${idx}`}
                                type="button"
                                disabled={subDisabled}
                                onClick={async () => {
                                  if (!subDisabled) {
                                    if (timerFeatureEnabled && canBeTimerForAssignEquipment(code)) {
                                      openTimerConfig({
                                        auxNum: selectedSlot!.index,
                                        typeCode: code,
                                        auxName: subLabel,
                                        lightTypeMode: 0,
                                        title: selectedSlot?.label || subLabel,
                                      });
                                    } else if (selectedSlot?.isRs485 && code === AuxTypeCode.RS485) {
                                      await submitRs485Assign(true);
                                    } else {
                                      await submitAuxAssign({
                                        auxNum: selectedSlot!.index,
                                        typeCode: code,
                                        auxName: subLabel,
                                        lightTypeMode: 0,
                                        auxTimerDurationSec: 0,
                                      });
                                    }
                                  }
                                }}
                                className={`w-full flex items-center justify-between px-2 py-2 rounded-md ${
                                  subDisabled
                                    ? 'bg-gray-100 dark:bg-gray-800 opacity-70 cursor-not-allowed border border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-400'
                                    : selected
                                    ? 'bg-green-50 dark:bg-green-900/20 ring-2 ring-green-500 border border-green-400'
                                    : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 border border-gray-300 dark:border-gray-700'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {subDisabled ? (
                                    <FiLock className="h-4 w-4 text-gray-400" />
                                  ) : selected ? (
                                    <FiCheckCircle className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <span className="inline-block h-4 w-4 rounded-full border-2 border-green-500" />
                                  )}
                                  <span className={`text-sm ${selected ? 'text-green-700 dark:text-green-400 font-medium' : ''}`}>{subLabel}</span>
                                </div>
                              </button>
                            );
                          })
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    {/* Pump confirm dialog */}
      <Dialog open={confirmPumpOpen} onClose={() => setConfirmPumpOpen(false)}>
        <DialogTitle>Confirm Pump Settings</DialogTitle>
        <DialogContent>
          <div className="text-sm text-gray-700 dark:text-gray-200 space-y-2">
            <div>
              <span className="font-medium">Pump Type:</span> "{pendingPumpName === 'SplashMe VSD' ? 'SplashMe VSD' : (pumpCategory ?? '-') }"
            </div>
            <div>
              <span className="font-medium">Brand:</span> "{pendingPumpName === 'SplashMe VSD' ? '-' : (pumpCategory === 'Others' ? 'Single Speed' : (pumpBrand ?? '-') )}"
            </div>
            <div>
              <span className="font-medium">Model:</span> "{pendingPumpName === 'SplashMe VSD' ? '-' : (pumpCategory === 'Others' ? '-' : (pumpModel ?? '-') )}"
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPumpOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={confirmPumpAssign} color="primary" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={timerConfigOpen} onClose={closeTimerConfig} fullWidth maxWidth="sm">
        <DialogTitle>Timer Settings</DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-2">
            <div className="text-sm text-gray-700 dark:text-gray-200">
              Configure timer for {timerTarget?.title || timerTarget?.auxName || 'this equipment'}.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${
                  !timerEnabled
                    ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                    : 'border-gray-300 text-gray-700 dark:border-gray-700 dark:text-gray-200'
                }`}
                onClick={() => setTimerEnabled(false)}
              >
                Normal Mode
              </button>
              <button
                type="button"
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${
                  timerEnabled
                    ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                    : 'border-gray-300 text-gray-700 dark:border-gray-700 dark:text-gray-200'
                }`}
                onClick={() => setTimerEnabled(true)}
              >
                Timer Mode
              </button>
            </div>
            {timerEnabled && (
              <div className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <div className="grid grid-cols-2 gap-3">
                  <label className="space-y-1 text-sm">
                    <span className="block text-gray-500 dark:text-gray-400">
                      {timerHourMinuteSupported ? 'Hours' : 'Minutes'}
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={timerHourMinuteSupported ? timerHours : timerMinutes}
                      onFocus={(e) => e.currentTarget.select()}
                      onChange={(e) => {
                        const nextValue = parseTimerInput(
                          e.target.value,
                          timerHourMinuteSupported ? HM_MAX_AUX_TIMER_HOURS : LEGACY_MAX_AUX_TIMER_MINUTES
                        );
                        if (timerHourMinuteSupported) {
                          setTimerHours(nextValue);
                        } else {
                          setTimerMinutes(nextValue);
                        }
                      }}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    />
                  </label>
                  <label className="space-y-1 text-sm">
                    <span className="block text-gray-500 dark:text-gray-400">
                      {timerHourMinuteSupported ? 'Minutes' : 'Seconds'}
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={timerHourMinuteSupported ? timerMinutes : timerSeconds}
                      onFocus={(e) => e.currentTarget.select()}
                      onChange={(e) => {
                        const nextValue = parseTimerInput(
                          e.target.value,
                          timerHourMinuteSupported ? HM_MAX_AUX_TIMER_MINUTES : LEGACY_MAX_AUX_TIMER_SECONDS
                        );
                        if (timerHourMinuteSupported) {
                          setTimerMinutes(nextValue);
                        } else {
                          setTimerSeconds(nextValue);
                        }
                      }}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    />
                  </label>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Current timer: {formatAuxTimerDuration(
                    timerHourMinuteSupported ? timerHours * 3600 + timerMinutes * 60 : timerMinutes * 60 + timerSeconds,
                    timerHourMinuteSupported,
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTimerConfig} color="inherit">Cancel</Button>
          <Button
            onClick={handleConfirmTimerAssign}
            color="primary"
            variant="contained"
            disabled={timerEnabled && (
              timerHourMinuteSupported ? timerHours * 3600 + timerMinutes * 60 : timerMinutes * 60 + timerSeconds
            ) <= 0}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Sidebar>
  );
};

export default AssignEquipmentSidebar;
